import React, { useMemo, useState } from 'react'
import { Target } from 'lucide-react'
import { TopScorerEvent } from '../types/topScorer'
import { useTranslation } from '../hooks/use-i18n'

interface TopScorerPredictPanelProps {
  event: TopScorerEvent
  submitting: boolean
  onSelect: (playerName: string) => void
  formatEventDate: (dateString: string) => string
}

const TopScorerPredictPanel: React.FC<TopScorerPredictPanelProps> = ({
  event,
  submitting,
  onSelect,
  formatEventDate,
}) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = event.choices || []
    // No search: show first 40 to avoid rendering all 310 at once.
    // With search: return ALL matches so every player is reachable.
    if (!q) return list.slice(0, 40)
    return list.filter((name) => name.toLowerCase().includes(q))
  }, [event.choices, query])

  const isOpen = event.isActive && new Date() < new Date(event.eventDate)

  return (
    <>
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-3">
        <Target className="w-7 h-7 text-emerald-400" />
      </div>
      <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">
        {t('topScorer.title')}
      </h1>
      <h2 className="text-lg font-semibold text-white/90 mb-2">{event.name}</h2>
      <p className="text-white/80 text-sm mb-4">
        {t('topScorer.deadline', { date: formatEventDate(event.eventDate) })}
      </p>

      {!isOpen ? (
        <p className="text-white/80">{t('topScorer.closed')}</p>
      ) : (
        <>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('topScorer.searchPlaceholder')}
            className="w-full mb-3 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-primary"
          />
          <p className="text-white/60 text-xs mb-3">{t('topScorer.searchHint')}</p>
          <div className="max-h-56 sm:max-h-64 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2 pr-1">
            {filtered.map((name) => (
              <button
                key={name}
                type="button"
                disabled={submitting}
                onClick={() => onSelect(name)}
                className="text-left px-3 py-2.5 rounded-xl bg-primary/90 hover:opacity-90 disabled:opacity-50 text-primary-foreground text-sm font-medium truncate"
              >
                {name}
              </button>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-white/70 text-sm text-center py-4">{t('topScorer.noResults')}</p>
          )}
        </>
      )}
    </>
  )
}

export default TopScorerPredictPanel
